import { MatMenuModule } from '@angular/material/menu';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CookieModule } from 'ngx-cookie';
import { RecaptchaModule, RecaptchaFormsModule, RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { provideAngularQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { providers } from './aggregators/providers';
import { oldPages } from './aggregators/old-pages';
import { pages, routes } from './aggregators/pages';
import { proWebsiteRoutes, redirects } from 'src/app/aggregators/redirects';
import { environment } from 'src/environments/environment';

import { SharedModule } from '../shared/shared.module';
import { TidyHomekeepingAppComponent } from './app.component';
import { RouteReuseStrategy, RouterModule, NoPreloading, Router, ActivatedRoute } from '@angular/router';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatPaginatorModule } from '@angular/material/paginator';
import { TimeoutErrorHandler } from 'src/shared/providers/timeout-error-handler';
import { ChunkLoadErrorHandler } from 'src/shared/providers/chunk-load-error-handler';
import { MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { CustomRouteReuseStrategy } from 'src/custom-route-reuse-strategy';
import { BookingRedirectGuard, bookingRedirectGuardFactory } from 'src/providers/guard/booking-redirect.guard';
import { HttpInterceptorService } from './interceptors/http.service';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { TimeoutInterceptor } from './interceptors/timeout.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { RateLimitInterceptor } from 'src/shared/interceptors/rate-limit.interceptor';
import { DemoInterceptor } from 'src/app/interceptors/demo.interceptor';
import { RequestsInFlightInterceptor } from 'src/app/interceptors/requests-in-flight.interceptor';
import * as Sentry from '@sentry/angular';
import { ViewTransitionSplashComponent } from 'src/shared/components/view-transition-splash/view-transition-splash.component';
import { CapacitorTestModal } from 'src/shared/components/capacitor-test-modal/capacitor-test-modal';
import { FloorplanViewerComponent } from 'src/shared/components/floorplan-viewer/floorplan-viewer.component';
import { SubscriptionTypePipe } from 'src/shared/pipes/subscription-type.pipe';
import { SubscriptionPlanCardComponent } from 'src/shared/components/subscription-plan-card/subscription-plan-card.component';
import { RequestTimeRangeListPipe } from 'src/shared/pipes/request-time-range-list.pipe';
import { ServiceLabelPipe } from 'src/shared/pipes/service-label.pipe';
import { NotBookableHeaderPipe } from 'src/shared/pipes/not-bookable-header.pipe';
import { JobChecklistDisplayComponent } from 'src/shared/components/job-checklist-display/job-checklist-display.component';
import { RestockTasksDisplayComponent } from 'src/shared/components/restock-tasks-display/restock-tasks-display.component';
import { ChannelsTabComponent } from 'src/pages/marketing/components/channels-tab/channels-tab.component';
import { AgentActivityListComponent } from 'src/shared/components/agent-activity-list/agent-activity-list.component';
import { ChannelDetailComponent } from 'src/pages/marketing/components/channel-detail/channel-detail.component';
import { UpsellsTabComponent } from 'src/pages/marketing/components/upsells-tab/upsells-tab.component';
import { WebsiteTabComponent } from 'src/pages/marketing/components/website-tab/website-tab.component';
import { GuidebookUpsellConfigComponent } from 'src/pages/marketing/components/guidebook-upsell-config/guidebook-upsell-config.component';
import { GuidebookPreviewComponent } from 'src/pages/marketing/components/guidebook-preview/guidebook-preview.component';
import { WebsiteDomainSettingsComponent } from 'src/pages/marketing/components/website-domain-settings/website-domain-settings.component';
import { WebsiteCustomDomainComponent } from 'src/pages/marketing/components/website-custom-domain/website-custom-domain.component';
import { WebsiteBookingLinkComponent } from 'src/pages/marketing/components/website-booking-link/website-booking-link.component';
import { GuidebookConfigEditorComponent } from 'src/pages/marketing/components/guidebook-config-editor/guidebook-config-editor.component';
import { GuidebookPage } from 'src/pages/guidebook/guidebook.page';
import { GuidebookSectionEditorComponent } from 'src/pages/guidebook/guidebook-section-editor/guidebook-section-editor.component';
import { ReviewsTabComponent } from 'src/pages/marketing/components/reviews-tab/reviews-tab.component';
import { ReviewDetailComponent } from 'src/pages/marketing/components/review-detail/review-detail.component';
import { LeaveGuestReviewComponent } from 'src/pages/marketing/components/leave-guest-review/leave-guest-review.component';
import { WifiTabComponent } from 'src/pages/marketing/components/wifi-tab/wifi-tab.component';
import { HappinessTabComponent } from 'src/pages/marketing/components/happiness-tab/happiness-tab.component';
import { ListingOptimizationSelectComponent } from 'src/pages/marketing/components/listing-optimization-select/listing-optimization-select.component';
import { WifiRouterStatusPage } from 'src/pages/marketing/components/wifi-router-status/wifi-router-status.page';
import { AutomaticMessagesTabComponent } from 'src/pages/messages/automatic-messages/automatic-messages.component';
import { BillsContentComponent } from 'src/shared/components/bills-content/bills-content.component';
import { ComplianceContentComponent } from 'src/pages/compliance/components/compliance-content/compliance-content.component';
import { GuestsTabComponent } from 'src/pages/my-pros/components/guests-tab/guests-tab.component';
import { VendorsTabComponent } from 'src/pages/my-pros/components/vendors-tab/vendors-tab.component';
import { TidySignaturePadComponent } from 'src/shared/components/signature-pad/signature-pad.component';
import { CustomerGuestsTabComponent } from 'src/pages/my-pros/components/customer-guests-tab/customer-guests-tab.component';
import { NoiseMonitoringContentComponent } from 'src/pages/compliance/components/noise-monitoring-content/noise-monitoring-content.component';
import { RenterAgreementsContentComponent } from 'src/pages/compliance/components/renter-agreements-content/renter-agreements-content.component';
import { PaymentsConfigContentComponent } from 'src/shared/components/payments-config-content/payments-config-content.component';
import { TaxesContentComponent } from 'src/shared/components/taxes-content/taxes-content.component';
import { MonthlyContentComponent } from 'src/shared/components/monthly-content/monthly-content.component';
import { InsuranceContentComponent } from 'src/shared/components/insurance-content/insurance-content.component';
import { DepositsContentComponent } from 'src/shared/components/deposits-content/deposits-content.component';
import { ReceivablesContentComponent } from 'src/shared/components/receivables-content/receivables-content.component';
import { UnitsContentComponent } from 'src/shared/components/units-content/units-content.component';
import { PaymentsContentComponent } from 'src/shared/components/payments-content/payments-content.component';
import { LeasesContentComponent } from 'src/shared/components/leases-content/leases-content.component';
import { TransactionsContentComponent } from 'src/shared/components/transactions-content/transactions-content.component';
import { ChannelSyncIndicatorComponent } from 'src/shared/components/channel-sync-indicator/channel-sync-indicator.component';
import { KpiTileComponent } from 'src/shared/components/kpi-tile/kpi-tile.component';
import { ChannelSyncDetailsPage } from 'src/pages/channel-sync-details/channel-sync-details';
import { EditPricingPage } from 'src/pages/marketing/components/edit-pricing/edit-pricing.page';
import { LazyChartComponent } from 'src/shared/components/lazy-chart/lazy-chart.component';
import { AccessContentComponent } from 'src/pages/properties/components/access-content/access-content.component';
import { UpgradesContentComponent } from 'src/pages/properties/components/upgrades-content/upgrades-content.component';
import { OngoingChecklistComponent } from 'src/shared/components/ongoing-checklist/ongoing-checklist.component';
import { ReservationCardComponent } from 'src/pages/schedule/components/reservation-card/reservation-card.component';
import { OwnerBlockCardComponent } from 'src/pages/schedule/components/owner-block-card/owner-block-card.component';
import { ReservationPopoverComponent } from 'src/pages/schedule/components/reservation-popover/reservation-popover.component';
import { ProRequirementsComponent } from 'src/shared/components/pro-requirements/pro-requirements.component';
import { InquiryDeclineModalComponent } from 'src/shared/components/inquiry-decline-modal/inquiry-decline-modal.component';
import { PreviewRibbonComponent } from 'src/shared/components/preview-ribbon/preview-ribbon.component';
import { GuestPickerComponent } from 'src/shared/components/guest-picker/guest-picker.component';
import { DemoTourComponent } from 'src/shared/components/demo-tour/demo-tour.component';
import { HalloweenComponent } from 'src/shared/components/halloween/halloween.component';
import { MonthAttributionSwitchComponent } from 'src/shared/components/month-attribution-switch/month-attribution-switch.component';

@NgModule({
    declarations: [
        ...oldPages,
        ...pages,
        ViewTransitionSplashComponent,
        CapacitorTestModal
    ],
    imports: [
        BrowserModule,
        HttpClientModule,
        ReactiveFormsModule,
        FormsModule,
        MatTooltipModule,
        MatButtonModule,
        MatPaginatorModule,
        MatMenuModule,
        MatTreeModule,
        MatIconModule,
        MatCheckboxModule,
        SharedModule,
        BrowserAnimationsModule,
        MatDialogModule,
        RecaptchaModule,
        DragDropModule,
        RecaptchaFormsModule,
        IonicModule.forRoot(),
        CookieModule.forRoot(),
        ScrollingModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        RouterModule.forRoot([...routes, ...redirects, ...proWebsiteRoutes], { preloadingStrategy: NoPreloading, onSameUrlNavigation: 'reload', useHash: false, enableTracing: false }),
        FloorplanViewerComponent,
        SubscriptionPlanCardComponent,
        SubscriptionTypePipe,
        RequestTimeRangeListPipe,
        ServiceLabelPipe,
        NotBookableHeaderPipe,
        JobChecklistDisplayComponent,
        RestockTasksDisplayComponent,
        ChannelsTabComponent,
        AgentActivityListComponent,
        ChannelDetailComponent,
        UpsellsTabComponent,
        WebsiteTabComponent,
        GuidebookUpsellConfigComponent,
        GuidebookPreviewComponent,
        WebsiteDomainSettingsComponent,
        WebsiteCustomDomainComponent,
        WebsiteBookingLinkComponent,
        GuidebookConfigEditorComponent,
        GuidebookPage,
        GuidebookSectionEditorComponent,
        ReviewsTabComponent,
        ReviewDetailComponent,
        LeaveGuestReviewComponent,
        WifiTabComponent,
        HappinessTabComponent,
        ListingOptimizationSelectComponent,
        WifiRouterStatusPage,
        EditPricingPage,
        AutomaticMessagesTabComponent,
        BillsContentComponent,
        ComplianceContentComponent,
        GuestsTabComponent,
        VendorsTabComponent,
        TidySignaturePadComponent,
        CustomerGuestsTabComponent,
        NoiseMonitoringContentComponent,
        RenterAgreementsContentComponent,
        PaymentsConfigContentComponent,
        TaxesContentComponent,
        MonthlyContentComponent,
        MonthAttributionSwitchComponent,
        InsuranceContentComponent,
        DepositsContentComponent,
        ReceivablesContentComponent,
        UnitsContentComponent,
        PaymentsContentComponent,
        LeasesContentComponent,
        TransactionsContentComponent,
        ChannelSyncIndicatorComponent,
        KpiTileComponent,
        ChannelSyncDetailsPage,
        LazyChartComponent,
        AccessContentComponent,
        OngoingChecklistComponent,
        ReservationCardComponent,
        OwnerBlockCardComponent,
        ReservationPopoverComponent,
        ProRequirementsComponent,
        InquiryDeclineModalComponent,
        GuestPickerComponent,
        UpgradesContentComponent,
        DemoTourComponent,
        HalloweenComponent,
        PreviewRibbonComponent
    ],
    bootstrap: [TidyHomekeepingAppComponent],
    providers: [
        ...providers,
        provideAngularQuery(new QueryClient({
          defaultOptions: {
            queries: {
              staleTime: 30_000,
              gcTime: 5 * 60_000,
              retry: 1,
              refetchOnWindowFocus: false,
            },
          },
        })),
        { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy},
        { provide: RECAPTCHA_SETTINGS, useValue: { siteKey: environment.recaptcha_site_key } as RecaptchaSettings },
        { provide: MatDialogRef, useValue: {} },
        { provide: BookingRedirectGuard, useFactory: bookingRedirectGuardFactory },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: RequestsInFlightInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: DemoInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: RateLimitInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: HttpInterceptorService,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: AuthInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: TimeoutInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ErrorInterceptor,
          multi: true
        },
        {
          provide: ErrorHandler,
          useClass: ChunkLoadErrorHandler,
        },
        {
          provide: Sentry.TraceService,
          deps: [Router],
        },
        {
          provide: APP_INITIALIZER,
          useFactory: () => () => {
            // Sentry trace initialization
          },
          deps: [Sentry.TraceService],
          multi: true,
        },
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    timeoutErrorHandler: TimeoutErrorHandler,
    _trace: Sentry.TraceService
    ) {
    AppRouter = router;
    AppActivatedRoute = activatedRoute;
    AppTimeoutHandler = timeoutErrorHandler;
  }
}
export let AppRouter: Router;
export let AppActivatedRoute: ActivatedRoute;
export let AppTimeoutHandler: TimeoutErrorHandler
